/**
 * Sole-trader super field requirements aligned with User/Super/Super.vue
 * (fund type: Industry = 2, SMSF = 1).
 */

export const SUPER_TYPE_SMSF = 1;
export const SUPER_TYPE_INDUSTRY = 2;

function isFilled(value) {
    return value != null && String(value).trim() !== '';
}

/**
 * @param {number|string|null|undefined} type
 * @returns {number|null}
 */
export function normalizeSuperFundType(type) {
    if (type == null || type === '') {
        return null;
    }
    const numericType = Number(type);
    if (numericType === SUPER_TYPE_SMSF || numericType === SUPER_TYPE_INDUSTRY) {
        return numericType;
    }
    return null;
}

/**
 * Common fields for all sole-trader super records (any fund type).
 *
 * @returns {Array<{ key: string, getValue: (locumSuper: object) => * }>}
 */
function commonSuperRules() {
    return [
        {
            key: 'super_type',
            getValue: (locumSuper) => locumSuper?.type,
            isComplete: (value) => normalizeSuperFundType(value) != null,
        },
        {
            key: 'super_superfund',
            getValue: (locumSuper) => locumSuper?.superfund,
            isComplete: (value) => isFilled(value),
        },
        {
            key: 'super_full_name',
            getValue: (locumSuper) => locumSuper?.fullName,
            isComplete: (value) => isFilled(value),
        },
        {
            key: 'super_birthday',
            getValue: (locumSuper) => locumSuper?.birthday,
            isComplete: (value) => isFilled(value),
        },
        {
            key: 'super_tfn',
            getValue: (locumSuper) => locumSuper?.tfn,
            isComplete: (value) => isFilled(value),
        },
        {
            key: 'super_abn',
            getValue: (locumSuper) => locumSuper?.abn,
            isComplete: (value) => isFilled(value),
        },
    ];
}

function industrySuperRules() {
    return [
        {
            key: 'super_usi',
            getValue: (locumSuper) => locumSuper?.usi,
            isComplete: (value) => isFilled(value),
        },
        {
            key: 'super_membership_number',
            getValue: (locumSuper) => locumSuper?.membershipNumber,
            isComplete: (value) => isFilled(value),
        },
    ];
}

function smsfSuperRules() {
    return [
        {
            key: 'super_esa',
            getValue: (locumSuper) => locumSuper?.esa,
            isComplete: (value) => isFilled(value),
        },
        {
            key: 'super_bsb',
            getValue: (locumSuper) => locumSuper?.bsb,
            isComplete: (value) => isFilled(value),
        },
        {
            key: 'super_account_number',
            getValue: (locumSuper) => locumSuper?.accountNumber,
            isComplete: (value) => isFilled(value),
        },
    ];
}

/**
 * @param {object} locumSuper
 * @returns {Array<{ key: string, getValue: Function, isComplete: Function }>}
 */
export function getSuperRequirementRules(locumSuper = {}) {
    const fundType = normalizeSuperFundType(locumSuper?.type);
    const rules = commonSuperRules();

    if (fundType === SUPER_TYPE_INDUSTRY) {
        return [...rules, ...industrySuperRules()];
    }

    if (fundType === SUPER_TYPE_SMSF) {
        return [...rules, ...smsfSuperRules()];
    }

    return rules;
}

/**
 * @param {object} locumSuper
 * @returns {Array<{ key: string, route: string, complete: boolean }>}
 */
export function evaluateSuperRequirementsClient(locumSuper = {}) {
    return getSuperRequirementRules(locumSuper)
        .filter((rule) => !rule.isComplete(rule.getValue(locumSuper)))
        .map((rule) => ({
            key: rule.key,
            route: 'profile_super',
            complete: false,
        }));
}
